import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { fetchLoadingDataWith } from '@/lib/service';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';
import { getFaqList } from '@/lib/utils/landingData';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const LandingPageMainContent = dynamic(() => import('@/components/LandingPage'), {
  ssr: true,
});

export const getStaticPaths: GetStaticPaths = async () => {
  // 不预生成任何路径，全部按需生成（首次访问时生成并缓存）
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale, params } = context;
  const id = params?.id as string;

  // URL 合并以集中权重，避免分散权重
  if (id === 'stories') {
    return {
      redirect: {
        destination: `${locale === 'en' ? '' : `/${locale}`}/anonymous-instagram-story-viewer`,
        permanent: true,
      },
    };
  }
  // 308 重定向
  if (id === 'posts') {
    return {
      redirect: {
        destination: `${locale === 'en' ? '' : `/${locale}`}/web-viewer-for-instagram`,
        permanent: true,
      },
    };
  }

  const path = `/instagram-viewer/${id}`;

  // 1. 并行请求数据，添加 .catch() 防止构建失败
  const [localeRes, tdkRes, dataResRaw] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale).catch((): null => null),
  ]);

  // 2. 语言 Fallback：当前语言无数据时请求英文
  let dataRes = dataResRaw;
  if (!dataRes?.data && locale !== 'en') {
    dataRes = await fetchLoadingDataWith(path, 'en').catch((): null => null);
  }

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const parentPath = removeLastPathSegment(path);
  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);

  // INFO: 网络不稳定时 dataRes 可能为 null，getFaqList 需要防御性检查
  const hasFaqData = dataRes?.data?.faqList?.content_list || dataRes?.data?.faqList?.category_list;
  const resultFaqList = hasFaqData ? getFaqList(dataRes) : [];

  function getWebApplication() {
    if (id === 'post-likes') {
      return {
        name: 'Instagram Post Likes Viewer — DolphinRadar',
        url: 'https://www.dolphinradar.com/instagram-viewer/post-likes',
        applicationCategory: 'Instagram Viewer',
        description:
          "Easily view who liked any public Instagram post with Dolphin Radar's Likes Viewer. Track likes on Reels, photos, and more.",
        featureList: [
          'View Public Post Likes Fast',
          'See Who Liked a Public Post',
          'Spot High-Performing Content',
          'No Instagram Login Required',
          'Rich Data',
          'Privacy-Focused',
        ],
      };
    }

    if (id === 'highlights') {
      return {
        name: 'Instagram Highlights Viewer — DolphinRadar',
        url: 'https://www.dolphinradar.com/instagram-viewer/highlights',
        applicationCategory: 'Instagram Highlights Viewer',
        description:
          "DolphinRadar's Instagram Highlights Viewer lets you view Highlights and Stories anonymously from any public profile.",
        featureList: [
          'Highlights Viewer',
          'Highlights Downloader',
          'Story Viewer',
          'Story Downloader',
          'Post Viewer',
          'Post Downloader',
        ],
      };
    }

    return {
      name: 'Instagram Post Comments Viewer — DolphinRadar',
      url: 'https://www.dolphinradar.com/instagram-viewer/post-comments',
      applicationCategory: 'Instagram Viewer',
      description:
        "Explore Instagram post comments with Dolphin Radar's IG Comment Viewer. Fast, simple access to public comments across Reels, photos, and more.",
      featureList: [
        'Easily View Public Post Comments',
        'Better Engagement Research',
        'No Instagram Login Required',
        'Rich Data',
      ],
    };
  }

  const jsonLd = page3FaqJsonLd(
    {
      title: hyphenatedToTitleCase(`${id}`),
      desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(SITE_HOST, path, locale),
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    },
    resultFaqList,
    getWebApplication()
  );
  // INFO: 注释掉 redirect 逻辑，因为在 getStaticProps (ISR) 构建时，
  // 如果网络不稳定导致 fetchLoadingDataWith 请求失败，dataRes 会为 null，
  // 此时触发 redirect 会导致构建失败。
  // 暂时允许 contentData 为空，页面组件应能处理空数据情况。
  // if (!dataRes?.data) {
  //   return {
  //     redirect: {
  //       destination: `/`,
  //       permanent: false,
  //     },
  //   };
  // }

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, path, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      contentData: dataRes?.data ?? null,
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('instagram-viewer/[id]'),
      __isr_page_name: 'instagram-viewer/[id]',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('instagram-viewer/[id]'),
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <LandingPageMainContent data={props} />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
